import React from 'react'
import PropTypes from 'prop-types'
import { Modal, Button, Form, Grid, Icon } from 'semantic-ui-react'
import {
  FilterModalContent,
  FilterChip,
  NoFiltersMessage,
  StyledModalActions,
} from './MarketplaceFilters.styled.js'
import { FILTER_TYPES } from './filterDefinitions.js'

// Filter components
import { DateRangeFilter } from './filters/DateRangeFilter.jsx'
import { CompanySelectFilter } from './filters/CompanySelectFilter.jsx'
import { UserSelectFilter } from './filters/UserSelectFilter.jsx'
import { CheckboxFilter } from './filters/CheckboxFilter.jsx'
import { CheckboxGroupFilter } from './filters/CheckboxGroupFilter.jsx'
import { FreightTypeFilter } from './filters/FreightTypeFilter.jsx'

/**
 * MarketplaceFiltersView
 *
 * Pure presentation component for the filters modal.
 * Uses Semantic UI Grid layout matching old admin-filters-form.
 */
export const MarketplaceFiltersView = ({
  isOpen,
  handleClose,
  availableFilterOptions,
  selectedFilterKey,
  currentFilterDef,
  selectedValue,
  handleFilterTypeSelect,
  handleValueChange,
  appliedFiltersWithDisplay,
  handleAddFilter,
  handleRemoveFilter,
  handleClearFilters,
  handleApplyFilters,
  isApplying,
  isClearing,
  isAddDisabled,
}) => {
  // Render the appropriate filter component based on type
  const renderFilterInput = () => {
    if (!currentFilterDef) return null

    const commonProps = {
      value: selectedValue,
      onChange: handleValueChange,
    }

    switch (currentFilterDef.key) {
      case FILTER_TYPES.DATE:
        return <DateRangeFilter {...commonProps} />

      case FILTER_TYPES.IMC:
        return (
          <CompanySelectFilter
            {...commonProps}
            companyType='imc'
            multiple={true}
            label='Select IMC Companies'
          />
        )

      case FILTER_TYPES.CARRIER:
        return (
          <CompanySelectFilter
            {...commonProps}
            companyType='carrier'
            multiple={false}
            label='Select Carrier'
          />
        )

      case FILTER_TYPES.DRIVER:
        return (
          <UserSelectFilter
            {...commonProps}
            userRole='driver'
            multiple={false}
            label='Select Driver'
          />
        )

      case FILTER_TYPES.CREATED_BY:
        return (
          <UserSelectFilter
            {...commonProps}
            hasCreatedOrders={true}
            multiple={true}
            label='Select Users'
          />
        )

      case FILTER_TYPES.EDI:
        return (
          <CheckboxFilter
            {...commonProps}
            label='EDI Filter'
            checkedLabel='FROM EDI'
            uncheckedLabel='NOT FROM EDI'
          />
        )

      case FILTER_TYPES.STREET_TURNS:
        return (
          <CheckboxGroupFilter
            {...commonProps}
            label='Show...'
            options={currentFilterDef.options}
          />
        )

      case FILTER_TYPES.LANE_LABEL:
        return (
          <CheckboxGroupFilter
            {...commonProps}
            label='Show...'
            options={currentFilterDef.options}
          />
        )

      case FILTER_TYPES.FREIGHT_TYPE:
        return (
          <FreightTypeFilter {...commonProps} label='Select Freight Type' />
        )

      case FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE:
        return (
          <Form.Field>
            <label>Show Tendered Orders</label>
            <div className='filter-info-hint'>
              Orders pending driver acceptance
            </div>
          </Form.Field>
        )

      default:
        return null
    }
  }

  const hasFilters = appliedFiltersWithDisplay.length > 0

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size='small'
      closeOnDimmerClick
      closeOnEscape
    >
      <Modal.Header>Apply Filters</Modal.Header>

      <Modal.Content>
        <FilterModalContent>
          <Form>
            <Grid divided='vertically'>
              {/* Applied Filters Row */}
              <Grid.Row>
                <Grid.Column>
                  {hasFilters ? (
                    <ul className='applied-filters'>
                      {appliedFiltersWithDisplay.map((filter) => (
                        <FilterChip key={filter.key}>
                          <strong>{filter.name}:</strong>{' '}
                          <span title={filter.displayValue}>
                            {filter.displayValue}
                          </span>
                          <Icon
                            name='remove'
                            className='remove-icon'
                            data-testid='marketplace-filters-remove-btn'
                            onClick={() => handleRemoveFilter(filter.key)}
                          />
                        </FilterChip>
                      ))}
                    </ul>
                  ) : (
                    <NoFiltersMessage>No Filters Selected</NoFiltersMessage>
                  )}
                </Grid.Column>
              </Grid.Row>

              {/* Filter Selection Row — only when filters are available */}
              {availableFilterOptions.length > 0 ? (
                <Grid.Row>
                  {/* Filter type dropdown */}
                  <Grid.Column width={6}>
                    <Form.Select
                      label='Filter'
                      placeholder='Select Filter'
                      data-testid='marketplace-filters-type-select'
                      value={selectedFilterKey || ''}
                      options={availableFilterOptions}
                      onChange={(e, { value }) => handleFilterTypeSelect(value)}
                    />
                  </Grid.Column>

                  {/* Filter value input */}
                  <Grid.Column
                    width={6}
                    data-testid='marketplace-filters-value-input'
                  >
                    {renderFilterInput()}
                  </Grid.Column>

                  {/* Add button */}
                  <Grid.Column width={4}>
                    <div className='add-filter-container'>
                      <Button
                        disabled={isAddDisabled}
                        onClick={handleAddFilter}
                        fluid
                        color='green'
                        data-testid='marketplace-filters-add-btn'
                        style={{ height: '38px' }}
                      >
                        Add Filter
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              ) : (
                <Grid.Row>
                  <Grid.Column className='no-available-filters'>
                    No Remaining Filters Available
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          </Form>
        </FilterModalContent>
      </Modal.Content>

      <StyledModalActions $hasFilters={hasFilters}>
        <Button
          color={hasFilters ? 'red' : 'green'}
          onClick={hasFilters ? handleClearFilters : handleClose}
          loading={isClearing}
          data-testid='marketplace-filters-clear-btn'
        >
          {hasFilters ? 'Clear Filters' : 'Close'}
        </Button>
        {hasFilters && (
          <Button
            color='green'
            onClick={handleApplyFilters}
            loading={isApplying}
            data-automation-id='apply-admin-filters'
            data-testid='marketplace-filters-apply-btn'
          >
            Apply &amp; Close
          </Button>
        )}
      </StyledModalActions>
    </Modal>
  )
}

MarketplaceFiltersView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  availableFilterOptions: PropTypes.array.isRequired,
  selectedFilterKey: PropTypes.string,
  currentFilterDef: PropTypes.object,
  selectedValue: PropTypes.any,
  handleFilterTypeSelect: PropTypes.func.isRequired,
  handleValueChange: PropTypes.func.isRequired,
  appliedFiltersWithDisplay: PropTypes.array.isRequired,
  handleAddFilter: PropTypes.func.isRequired,
  handleRemoveFilter: PropTypes.func.isRequired,
  handleClearFilters: PropTypes.func.isRequired,
  handleApplyFilters: PropTypes.func.isRequired,
  isApplying: PropTypes.bool,
  isClearing: PropTypes.bool,
  isAddDisabled: PropTypes.bool,
}

MarketplaceFiltersView.defaultProps = {
  selectedFilterKey: null,
  currentFilterDef: null,
  selectedValue: null,
  isApplying: false,
  isClearing: false,
  isAddDisabled: true,
}
